/**
 * Issue #761 § 1 — the decision the record's ratios rest on, in ONE place.
 *
 * `kit/NumberTile.jsx` already renders an absent figure as the em dash
 * (`resolveDisplay`/`NOT_RECORDED`) — what this module adds is the DECISION
 * a caller feeds it: is there a real ratio here, or is there nothing to
 * divide by. Two pure functions, no React, no fetch — the same reason
 * `lib/recordGate.js` (#760) and `lib/counters.js` (#715) are plain
 * functions of their inputs: a fact a unit test can pin without mounting a
 * component (this repo's own convention).
 *
 * **Ignore the API's own zero-fill.** `api/src/routes/statsCompute.ts`
 * still returns `avg`/`econ` zero-filled when the count behind them is
 * absent (`avg = dismissals ? … : notOuts ? totalRuns : 0`) — decision:
 * that file stays exactly as it is (issue #761, "explicitly out of
 * scope"), and every caller here must read the COUNTS
 * (`innings`/`not_outs`/`overs`) rather than trust the pre-divided number.
 * Never touch `api/`.
 *
 * DRAWN-STATE COPY LIVES HERE ONLY FOR THE ONE CASE THE DRAWING FIXES IT:
 * exactly one innings, dismissed (`OneInnings.dc.html:97-98`, brief-761.md
 * ruling 1) — `battingAverage` special-cases that one shape because the
 * drawn sentence ("Rests on one innings, and one innings averaged is that
 * innings again…") is not a filled-in template, it is its own sentence, and
 * the drawing also adds a second, static "Had you not been out…" line only
 * for that shape. Every other shape (what #762 will exercise, two or more
 * innings) gets the generic template the issue's own § 1 pseudocode gives.
 * The bowling card's own longer drawn paragraph
 * ("Wickets and overs are totals…") is NOT reproduced here — that prose
 * mixes a general truth with this one screen's own framing, so
 * `RecordOneMatchScreen.jsx` composes it itself from `economy()`'s `value`
 * and the raw overs/runs-conceded, keeping this module's own `restsOn` a
 * plain, reusable fragment.
 */

import { counterText } from "./counters";
import { oversToBalls, perOverRate } from "./overs";

const ONES = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
];
const TEENS = [
  "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen",
];
const TENS = [
  "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
];

/**
 * A non-negative integer, spelled out — `OneInnings.dc.html`'s own prose
 * convention (numbers as words, e.g. "four overs and eighteen runs", never
 * digits, per brief-761.md ruling 3). Handles 0-999, which covers every
 * plausible per-match cricket figure (overs, runs conceded, dismissals,
 * innings) this screen ever renders; a value that somehow exceeds that
 * falls back to digits rather than throwing — a defensive floor, not a
 * drawn case.
 */
export function numberToWords(n) {
  const v = Math.trunc(Math.abs(Number(n) || 0));
  if (v < 10) return ONES[v];
  if (v < 20) return TEENS[v - 10];
  if (v < 100) {
    const tens = Math.floor(v / 10);
    const rem = v % 10;
    return rem ? `${TENS[tens]}-${ONES[rem]}` : TENS[tens];
  }
  if (v < 1000) {
    const hundreds = Math.floor(v / 100);
    const rem = v % 100;
    return rem ? `${ONES[hundreds]} hundred and ${numberToWords(rem)}` : `${ONES[hundreds]} hundred`;
  }
  return String(v);
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Issue #945 (PR #945 review round 1, F1) — "the player batted", mirrored
 * from `api/src/routes/statsCompute.ts`'s `battedIn`/`notOutIn`/
 * `realDismissal` EXACTLY. `statsCompute.ts` is the source of truth for
 * this rule; these three exist only because this file's own header rule
 * ("ignore the API's own zero-fill... every caller here must read the
 * COUNTS") already has client-side screens (SeasonRecordScreen's tiles,
 * `groundCard.js`'s `groundBattingRecord`) recomputing innings/not-out/
 * strike-rate from the raw match list rather than trusting the API's
 * pre-divided figures — before this, they still used the OLD
 * `balls_faced > 0` test the API itself moved off of (#873), so the tiles
 * and the API-backed fold on the same screen could disagree. If
 * `statsCompute.ts`'s rule ever changes, mirror the change here too — same
 * edge-case table, same three names, snake_case fields (this file always
 * reads the raw API match shape: `batting.runs`, `batting.balls_faced`,
 * `batting.dismissal_type`, `batting.not_out`), never camelCase.
 *
 * Deliberately NOT used by `matchInningsFigures` below — that function's
 * own header explains why the one-match record screen keeps a narrower,
 * unrelated rule (brief-761.md ruling: any batting field present at all,
 * on the record's one and only match, is the whole innings count).
 */
export function realDismissal(match) {
  const dt = String(match?.batting?.dismissal_type ?? "").trim();
  return dt !== "" && dt.toLowerCase() !== "not out";
}
export function battedIn(match) {
  const runs = match?.batting?.runs;
  const balls = match?.batting?.balls_faced;
  return (Number(runs) || 0) > 0 || (Number(balls) || 0) > 0 || realDismissal(match);
}
export function notOutIn(match) {
  return battedIn(match) && !realDismissal(match) && match?.batting?.not_out === true;
}

/**
 * The batting-average decision (issue #761 § 1).
 *
 * @param {object} args
 * @param {number} [args.total_runs]
 * @param {number} [args.innings]
 * @param {number} [args.not_outs]
 * @returns {{
 *   value: number|null,
 *   dismissals: number,
 *   restsOn: string|null,
 *   hypotheticalNotOut: string|null,
 *   reason: string|null,
 * }}
 */
export function battingAverage({ total_runs, innings, not_outs } = {}) {
  const runs = Number(total_runs) || 0;
  const inn = Number(innings) || 0;
  const notOuts = Number(not_outs) || 0;
  const dismissals = inn - notOuts;

  if (dismissals > 0) {
    const value = round2(runs / dismissals);
    // Ruling 1 (brief-761.md): the drawn sentence for exactly one innings,
    // dismissed — not a template fill, the drawing's own words — plus the
    // hypothetical line that only makes sense to say for that one shape.
    const isOneInningsDismissed = inn === 1 && dismissals === 1;
    const restsOn = isOneInningsDismissed
      ? "Rests on one innings, and one innings averaged is that innings again. It will move as soon as there is a second."
      : `Rests on ${numberToWords(dismissals)} dismissal${dismissals === 1 ? "" : "s"} in ${numberToWords(inn)} inning${inn === 1 ? "" : "s"}.`;
    const hypotheticalNotOut = isOneInningsDismissed
      ? "Had you not been out, there would be no average here at all — not a nought, and not a wait. An average needs a dismissal to divide by, and you would have had none."
      : null;
    return { value, dismissals, restsOn, hypotheticalNotOut, reason: null };
  }

  if (inn > 0) {
    // Ruling 2: not out, so no average — this IS the issue's own helper
    // wording (§1), never the hypothetical line (it would be false here).
    return {
      value: null,
      dismissals,
      restsOn: null,
      hypotheticalNotOut: null,
      reason: "Not out, so there is no average yet — there is nothing to divide by.",
    };
  }

  return { value: null, dismissals, restsOn: null, hypotheticalNotOut: null, reason: "No innings yet." };
}

/**
 * The economy decision (issue #761 § 1).
 *
 * @param {object} args
 * @param {number} [args.runs_conceded]
 * @param {number} [args.overs]
 * @returns {{ value: number|null, restsOn: string|null, reason: string|null }}
 */
export function economy({ runs_conceded, overs } = {}) {
  const ov = Number(overs) || 0;
  const rc = Number(runs_conceded) || 0;

  if (ov > 0) {
    // #860: overs are overs and balls, so the rate divides by BALLS. An
    // overs figure that is not over.ball notation (3.7) has no economy; the
    // screens show a dash, and no reason is given (it is not "no overs").
    const balls = oversToBalls(ov);
    if (balls === null) return { value: null, restsOn: null, reason: null };
    const value = perOverRate(rc, balls);
    const base = Number.isInteger(ov) ? numberToWords(ov) : String(ov);
    const restsOn = `Rests on ${base} over${ov === 1 ? "" : "s"} and ${numberToWords(rc)} run${rc === 1 ? "" : "s"}.`;
    return { value, restsOn, reason: null };
  }

  return {
    value: null,
    restsOn: null,
    reason: "You bowled no overs, so there is no economy and no average here — blank, not nought. There is nothing to divide by.",
  };
}

/**
 * The bowling-average decision (issue #762 — `SeasonRecord.dc.html:141-144`
 * draws a Bowling average tile `OneInnings.dc.html` never had). Runs
 * conceded per wicket, never per over — `economy()` above already owns the
 * per-over rate, and this is deliberately its own function rather than a
 * second meaning bolted onto that one, the same "one function, one
 * question" discipline `battingAverage`/`economy` already split on.
 *
 * The drawn line ("Rests on 231 runs conceded off 44 overs, for 14 wickets.
 * Economy 5.25 on the same overs.") mixes THIS figure's own inputs with
 * `economy()`'s value in one sentence — composed by the caller
 * (`SeasonRecordScreen.jsx`), same precedent `RecordOneMatchScreen.jsx`'s
 * own `bowlingRestsOn` sets for its equivalent paragraph, so this function
 * returns only the plain value/reason and leaves prose composition there.
 *
 * @param {object} args
 * @param {number} [args.runs_conceded]
 * @param {number} [args.wickets]
 * @returns {{ value: number|null, reason: string|null }}
 */
export function bowlingAverage({ runs_conceded, wickets } = {}) {
  const w = Number(wickets) || 0;
  const rc = Number(runs_conceded) || 0;

  if (w > 0) {
    return { value: round2(rc / w), reason: null };
  }

  return { value: null, reason: "No wickets yet, so there is no bowling average — there is nothing to divide by." };
}

/**
 * The strike-rate decision (issue #762 § "the four" — no equivalent existed
 * before this slice: #761's `OneInnings.dc.html` never drew a Strike Rate
 * tile, so `battingAverage`/`economy` above had no sibling for it. Added
 * here, in the same module, for the same reason those two live here: a
 * caller must read the RAW counts (runs, balls faced) rather than trust
 * `/stats/summary`'s own pre-divided `sr` field, which zero-fills exactly
 * like `avg`/`econ` do (`api/src/routes/statsCompute.ts`) and is never
 * touched — `api/` stays out of scope.
 *
 * `/stats/summary` does not expose a raw balls-faced total at all (only
 * the pre-divided `sr`), so `SeasonRecordScreen.jsx` derives `runs`/`balls`
 * itself by summing `batting.runs`/`batting.balls_faced` across the
 * (season/competition/team-filtered) `matches` array `Dashboard.jsx`
 * already holds — the same "read the match record" approach
 * `matchInningsFigures` uses one match at a time.
 *
 * @param {object} args
 * @param {number} [args.runs]
 * @param {number} [args.balls]
 * @returns {{ value: number|null, restsOn: string|null, reason: string|null }}
 */
export function strikeRate({ runs, balls } = {}) {
  const b = Number(balls) || 0;
  const r = Number(runs) || 0;

  if (b > 0) {
    const value = round2((r / b) * 100);
    const restsOn = `Rests on ${r} run${r === 1 ? "" : "s"} off ${b} ball${b === 1 ? "" : "s"}.`;
    return { value, restsOn, reason: null };
  }

  return {
    value: null,
    restsOn: null,
    reason: "No balls faced yet, so there is no strike rate — there is nothing to divide by.",
  };
}

/**
 * Issue #945 (PR #945 review, F1) — `SeasonRecordScreen.jsx`'s own tile
 * arithmetic, extracted so it can be pinned directly rather than only
 * through its caller's rendered numbers. This IS the shape of the F1
 * regression: the screen used to compute innings/strike-rate inline, using
 * the API's OLD `balls_faced > 0` rule (pre-#873), and drifted the moment
 * statsCompute.ts moved off it — nothing caught it because nothing tested
 * this arithmetic on its own, only the two screens' rendered output
 * separately. One function, one test, both callers (the season tiles and
 * anything else that needs the same batting figures from a raw match
 * list) share it from here on.
 *
 * @param {Array<{batting?: {runs?: number|null, balls_faced?: number|null, not_out?: boolean|null, dismissal_type?: string|null}}>} matches
 * @returns {{ innings: number, notOuts: number, totalRuns: number, highest: number|null, sr: object, avg: object }}
 */
export function seasonBattingFigures(matches) {
  const list = matches || [];
  const runsOf = (m) => m.batting?.runs;
  const ballsFacedOf = (m) => m.batting?.balls_faced;
  const sum = (fn) => list.reduce((acc, m) => acc + (fn(m) || 0), 0);
  const battingInnings = list.filter(battedIn);
  const totalRuns = sum(runsOf);
  const totalBalls = sum(ballsFacedOf);
  // Same split as statsCompute.ts's ballsRuns (#873): strike rate's
  // numerator is only runs from matches with a REAL ball count, so an
  // innings counted for average (totalRuns, above) but with no balls
  // recorded never inflates strike rate.
  const ballsRuns = sum((m) => ((ballsFacedOf(m) || 0) > 0 ? runsOf(m) : 0));
  const notOuts = battingInnings.filter(notOutIn).length;
  const highest = battingInnings.length ? Math.max(...battingInnings.map((m) => runsOf(m) || 0)) : null;
  return {
    innings: battingInnings.length,
    notOuts,
    totalRuns,
    highest,
    sr: strikeRate({ runs: ballsRuns, balls: totalBalls }),
    avg: battingAverage({ total_runs: totalRuns, innings: battingInnings.length, not_outs: notOuts }),
  };
}

/**
 * The hero line under Total Runs (issue #761; PR #857 review, F1).
 *
 * BUILT ONLY FROM PARTS THAT EXIST. The review's own repro: a player who
 * bowled but never batted has `match.batting.balls_faced`/`dismissal_type`
 * both absent, and the naive template produced "Off — balls, , at GROUND
 * on DATE" — a stray double-comma and a dash sitting inside a sentence,
 * which is exactly the "absent rendered as a value-shaped placeholder"
 * mistake this whole issue exists to refuse elsewhere. Each fragment
 * (balls faced, dismissal, ground/date) is included only when it is
 * actually there; a genuinely empty innings does not fall through to a
 * half-built sentence at all — see below.
 *
 * NO INNINGS AT ALL. `hasInnings` is the caller's own answer to "did this
 * match have any batting recorded" (`RecordOneMatchScreen.jsx` derives it
 * from `match.batting.runs`/`balls_faced`, NOT `summary.innings` —
 * `statsCompute.ts` only counts an innings when `balls_faced > 0`, but a
 * player can log real runs without also entering balls faced, the same
 * #715 "a blank counter is not a nought" gap one level up; see that
 * file's own comment for the journey this broke before the fix). When
 * there is no innings at all, the line says so plainly instead of
 * describing one that never happened.
 *
 * @param {object} args
 * @param {boolean} args.hasInnings — the caller's own "did they bat in
 *   this match" answer (see above) — this function trusts it as given.
 * @param {{ batting?: object, ground?: string, match_date?: string }|null} [args.match]
 * @returns {string} empty string when there is no match to describe yet
 *   (`recentMatchesFailed` — the caller omits this line entirely then).
 */
export function heroLine({ hasInnings, match, closing = true } = {}) {
  if (!match) return "";

  if (!hasInnings) {
    return closing
      ? "You didn't bat in this match. That is everything the record holds, and it holds it exactly."
      : "You didn't bat in this match.";
  }

  const batting = match.batting || {};
  const segments = [];
  if (batting.balls_faced != null) segments.push(`Off ${batting.balls_faced} balls`);
  const dismissalWord = String(batting.dismissal_type || (batting.not_out ? "Not Out" : "")).toLowerCase();
  if (dismissalWord) segments.push(dismissalWord);
  if (match.ground && match.match_date) segments.push(`at ${match.ground} on ${match.match_date}`);
  else if (match.ground) segments.push(`at ${match.ground}`);
  else if (match.match_date) segments.push(`on ${match.match_date}`);

  // PR #857 review round 2, B2 — the sentence's FIRST segment is not
  // always "Off N balls" (already capitalised as a literal string): when
  // balls_faced is missing, the line can start with the dismissal word
  // ("caught, at …") or the ground clause ("at …"), both lower case.
  // Capitalise whichever fragment actually lands first, rather than
  // hardcoding a fix for "Off" alone.
  if (segments.length > 0) {
    segments[0] = segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
  }

  const lead = segments.join(", ");
  // Issue #1217 — `closing: false` while the career totals failed to load:
  // "everything the record holds" under a dashed Total Runs reads wrong.
  if (!closing) return lead ? `${lead}.` : "";
  return lead
    ? `${lead}. That is everything the record holds, and it holds it exactly.`
    : "That is everything the record holds, and it holds it exactly.";
}

/**
 * The one-match batting figures Innings and the batting average actually
 * need (issue #761; PR #857 review round 2, B1) — derived from the SINGLE
 * match record, never from `summary.innings`/`summary.not_outs`.
 *
 * THE BUG THIS FIXES. `statsCompute.ts` only counts an innings when
 * `balls_faced > 0`. A player who entered runs without also entering
 * balls faced — or a genuine duck who DID enter balls faced but whose
 * `balls_faced` still reads 0/null for some other reason — both read as
 * "zero innings" at the API, which fed `battingAverage` a false
 * `innings: 0` and put "No innings yet." (or the not-out reason lost
 * entirely) next to a real, non-zero Total Runs. This screen already
 * knows there is exactly one match on the whole record — `matches === 1`
 * is the gate that put it here (`lib/recordGate.js`) — so when the match
 * record shows ANY batting at all, that one match IS the whole innings
 * count, full stop; no need to ask the API's own (differently-gated)
 * count at all.
 *
 * `totalRuns` here exists ONLY to feed `battingAverage` — it is NOT a
 * second source for the Total Runs hero figure, which stays on
 * `summary.total_runs` (`sum(matches, m => m.batting.runs)`, which is NOT
 * gated on `balls_faced` and was never wrong).
 *
 * @param {{ batting?: { runs?: number|null, balls_faced?: number|null, not_out?: boolean|null } }|null} match
 * @returns {{ hasInnings: boolean, innings: number, notOuts: number, totalRuns: number }}
 */
export function matchInningsFigures(match) {
  const batting = match?.batting || {};
  const hasInnings = batting.runs != null || batting.balls_faced != null;
  if (!hasInnings) {
    return { hasInnings: false, innings: 0, notOuts: 0, totalRuns: 0 };
  }
  return {
    hasInnings: true,
    innings: 1,
    notOuts: batting.not_out ? 1 : 0,
    totalRuns: batting.runs ?? 0,
  };
}

/**
 * Issue #704 closes the gap #901 (D141) worked around. `GET
 * /analytics/career`'s `wicketkeeping.matches` now counts
 * `matches.kept_wicket === true` (`api/src/routes/statsCompute.ts`), not
 * every match played — the real flag the "I was the wicketkeeper" tick
 * saves (`matches.kept_wicket`, migration 0066), not an activity proxy. So
 * "has this account ever kept" is now the flag's own count, not an inferred
 * sum over five fielding figures that could be zero on a genuine clean
 * keeping game. Kept as a named function (same name, same callers —
 * `CareerDashboard.jsx`, `SeasonRecordScreen.jsx`) because both still ask
 * exactly this question; only the answer's source changed.
 *
 * @param {{ matches?: number }|null} [wk]
 * @returns {boolean}
 */
export function keptWicketEver(wk) {
  return Number(wk?.matches) > 0;
}

/**
 * D102: "Strike rate shows one decimal on rebuilt screens; economy keeps
 * two." Two separate formatters, not one shared "rate" helper carrying an
 * exception — `toRateText` (economy only, as of D184; see below) and
 * `srText` (one decimal, strike rate only) are both D102-compliant, not a
 * deviation from it.
 *
 * Review round 3, F4 — this pair used to be defined three times
 * (`SeasonRecordScreen.jsx`, `RecordOneMatchScreen.jsx`, `MatchRow.jsx`),
 * each a byte-for-byte copy of the other two. One definition, imported by
 * all three: the decision this module already exists to hold in one place
 * (this file's own header), and a caller shaping its own copy to keep a
 * source-level test pinned on a duplicate is exactly the detector-evasion
 * pattern the "no drift" reviews here refuse.
 *
 * D184 (issue #963, Andreas 2026-09-30) narrowed `toRateText` to economy
 * alone: most round-5 drawings print every AVERAGE (batting, bowling,
 * squad) to one decimal — `GroundCard.dc.html`'s "31.7", `Squad.dc.html`'s
 * "avg 27.4"/"avg 34.1"/"avg 14.0" (a whole-number average still carries
 * its trailing zero, never bare "14"), `CoachPlayerRecord.dc.html`'s
 * career-snapshot "Average" tile at "37.5" — D184 is the decision that
 * this applies EVERYWHERE, including the two artboards that still draw an
 * average at two decimals (`OneInnings.dc.html`'s "34.00",
 * `SeasonRecord.dc.html`'s "16.50"/"43.00") — those predate this decision
 * and were never redrawn to match once D102 first drew that line; this
 * issue closes that gap in code rather than waiting on new artwork.
 * Economy's own drawn value is untouched. `avgText` below is the ONE
 * shared average formatter this issue asks for; `toRateText` keeps its
 * name and its two-decimal shape for economy's sake only, rather than
 * being deleted and reintroduced as a second "two-decimal" function under
 * a new name — same value, same callers (`MatchRow.jsx`'s Econ cell,
 * `RecordOneMatchScreen.jsx`/`SeasonRecordScreen.jsx`'s Economy tiles),
 * fewer of them than before.
 */
export function toRateText(value) {
  return value == null ? counterText(value) : counterText(value.toFixed(2));
}
export function srText(value) {
  return value == null ? counterText(value) : counterText(value.toFixed(1));
}
/**
 * D184 (issue #963) — the one shared average formatter: batting average,
 * bowling average, and the squad/coach-row average all move through this,
 * never a second `toFixed` scattered per caller. One decimal, always
 * shown (`(31.666…).toFixed(1)` is `"31.7"`; `(14).toFixed(1)` is `"14.0"`,
 * matching `Squad.dc.html`'s own drawn whole-number case) — and `null`/
 * `undefined` fall through to `counterText`'s em dash, the same "no
 * average yet" blank rule every caller here already keeps.
 */
export function avgText(value) {
  return value == null ? counterText(value) : counterText(value.toFixed(1));
}
